import { useSelector } from 'react-redux';
import { paths } from '../../common/constants/pathVariables';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { additionalServicesMsg, basicDetailsMsg, billingAccountAndUserMsg, devicesMsg, t } from '../../common/i18n';
import { StickyEppApproveOrDeny } from '../SubscriptionDetails/Components/StickyEppApproveOrDeny';
import { getDeviceTypeText } from '../SubscriptionDetails/subscriptionDetailsCommon';
import { MobileSubscriptionLayout, getSubscriptionUrl } from './MobileSubscriptionLayout';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store';
import type { LocalNaviItem } from '../LocalNavi';
import type { PropsWithChildren } from 'react';

const getNavigationCategories = (subscription: Subscription, mdmId: string, hideBillingAccountAndUser: boolean) => {
  const navigationCategories = [
    {
      children: t.C9M1(basicDetailsMsg),
      to: getSubscriptionUrl(paths.PS_DEVICE, subscription, mdmId),
    },
  ] as LocalNaviItem[];

  if (!hideBillingAccountAndUser) {
    navigationCategories.push({
      children: t.CZ63(billingAccountAndUserMsg),
      to: getSubscriptionUrl(paths.PS_DEVICE_SUBSCRIPTION_BA_CONTRACT_PARTY, subscription, mdmId),
    });
  }

  navigationCategories.push({
    children: t.LXSR(additionalServicesMsg),
    to: getSubscriptionUrl(paths.PS_DEVICE_ADDONS, subscription, mdmId),
  });

  return navigationCategories;
};

export type DeviceLayoutProps = PropsWithChildren<{
  companyInfo: CompanyInfoResponse;
  subscription: Subscription;
  hasPendingAdminReview: boolean;
}>;

const getHeroPicto = (subscriptionSubType = '') => {
  switch (subscriptionSubType.toLowerCase()) {
    case 'phone':
      return 'phone-filled';
    case 'tablet':
      return 'tablet-filled';
    case 'computers':
      return 'laptop-filled';
    case 'network equipment':
      return 'wireless-device-filled';
    case 'accessories':
      return 'headphones-filled';
    default:
      return 'generic-service-filled';
  }
};

export const DeviceLayout = ({ children, companyInfo, subscription, hasPendingAdminReview }: DeviceLayoutProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  if (!mdmId) {
    throw new Error('missing mdmId');
  }
  const { suspendSubscriptionInitiated, terminateSubscriptionInitiated } = useSelector((s: State) => ({
    suspendSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated ?? false,
    terminateSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated ?? false,
  }));
  const hideBillingAccountAndUser = suspendSubscriptionInitiated ?? terminateSubscriptionInitiated;

  const companyName = companyInfo?.companyName;
  const headingBottom = subscription?.subscriptionContactName
    ? `${subscription?.subscriptionContactName} | ${companyName}`
    : companyName;

  return (
    <MobileSubscriptionLayout
      subscription={subscription}
      headingTop={getDeviceTypeText(subscription.subscriptionSubType ?? '')}
      heading={subscription?.subscriptionName ?? ''}
      headingBottom={headingBottom}
      heroPicto={getHeroPicto(subscription.subscriptionSubType)}
      navigationItems={getNavigationCategories(subscription, mdmId, hideBillingAccountAndUser)}
      categoryBreadcrumbAndPath={{ name: t.JJTM(devicesMsg), path: paths.PS_DEVICES }}
    >
      {children}
      <StickyEppApproveOrDeny hasPendingAdminReview={hasPendingAdminReview} subscription={subscription} mdmId={mdmId} />
    </MobileSubscriptionLayout>
  );
};
